export type FeatureType =
  | "document_generation"
  | "consultation"
  | "case"
  | "exercise"
  | "exam"
  | "simulation"
  | "document_review"
  | "case_summary"
  | "legal_ai_chat"
  | "contract_generation";

export interface LimitCheckResult {
  allowed: boolean;
  remaining: number | null; // null = unlimited
  limit: number | null; // null = unlimited
  current: number;
  featureType: FeatureType;
  willUseCredits?: boolean; // true if next use will consume credits
  creditsNeeded?: number; // credits needed if limit reached
  creditsAvailable?: number; // current credits balance
}

/**
 * Check if user can perform an action based on their plan limits
 */
export async function checkLimit(
  userId: string,
  featureType: FeatureType
): Promise<LimitCheckResult> {
  // Dynamic import to avoid circular dependencies
  const {
    checkFeatureLimit,
    getUserSubscription,
    getPlanLimits,
    getUserUsage,
  } = await import("@/shared/db");

  const { getUserCreditsBalance, getFeatureCreditsCost, hasEnoughCredits } =
    await import("@/shared/db");

  const current = await getUserUsage(userId, featureType);
  const subscription = await getUserSubscription(userId);

  let limit: number | null = null;
  let remaining: number | null = null;
  let willUseCredits = false;
  let creditsNeeded = 0;
  let creditsAvailable = 0;

  if (subscription) {
    const limits = await getPlanLimits(subscription.plan_type);
    if (limits) {
      switch (featureType) {
        case "document_generation":
          limit = limits.documents_per_month;
          break;
        case "consultation":
          limit = limits.consultations_per_month;
          break;
        case "case":
          limit = limits.cases_per_month;
          break;
        case "exercise":
          limit = limits.exercises_per_month;
          break;
        case "exam":
          limit = limits.exams_per_month;
          break;
        case "simulation":
          limit = limits.simulations_per_month;
          break;
        case "document_review":
          limit = limits.document_reviews_per_month;
          break;
        case "case_summary":
          limit = limits.case_summaries_per_month;
          break;
        case "legal_ai_chat":
          limit = limits.legal_ai_queries_per_month ?? null;
          break;
        case "contract_generation":
          // Assuming this field exists in plan_limits table,
          // if not it will fallback to null (unlimited)
          limit = (limits as any).contract_generations_per_month;
          break;
      }
    }

    // Calculate remaining quota
    if (limit !== null) {
      remaining = Math.max(0, limit - current);
    } else {
      remaining = null; // Unlimited
    }

    // Check if limit is reached and if credits are available
    if (limit !== null && current >= limit) {
      // Limit reached, check credits
      creditsNeeded = getFeatureCreditsCost(featureType as FeatureType);
      creditsAvailable = await getUserCreditsBalance(userId);
      willUseCredits = await hasEnoughCredits(userId, creditsNeeded);
    }
  }

  // Check if action is allowed (either within limit or has credits)
  const allowed = await checkFeatureLimit(userId, featureType);

  return {
    allowed,
    remaining,
    limit,
    current,
    featureType,
    willUseCredits: willUseCredits && (limit === null || current >= limit),
    creditsNeeded: willUseCredits ? creditsNeeded : undefined,
    creditsAvailable: creditsAvailable > 0 ? creditsAvailable : undefined,
  };
}

/**
 * Get feature name for display
 */
export function getFeatureName(featureType: FeatureType): string {
  const names: Record<FeatureType, string> = {
    document_generation: "Document Generation",
    consultation: "Consultations",
    case: "Cases",
    exercise: "Exercises",
    exam: "Exams",
    simulation: "Simulations",
    document_review: "Document Reviews",
    case_summary: "Case Summaries",
    legal_ai_chat: "Legal AI Chat",
    contract_generation: "Contract Generation",
  };
  return names[featureType];
}

/**
 * Get feature name in Spanish
 */
export function getFeatureNameEs(featureType: FeatureType): string {
  const names: Record<FeatureType, string> = {
    document_generation: "Generación de Documentos",
    consultation: "Consultas",
    case: "Casos",
    exercise: "Ejercicios",
    exam: "Exámenes",
    simulation: "Simulaciones",
    document_review: "Revisiones de Documentos",
    case_summary: "Resúmenes de Casos",
    legal_ai_chat: "Chat Legal IA",
    contract_generation: "Generación de Contratos",
  };
  return names[featureType];
}

/**
 * Format limit display
 */
export function formatLimit(limit: number | null): string {
  if (limit === null) {
    return "Unlimited";
  }
  return limit.toString();
}

/**
 * Format limit display in Spanish
 */
export function formatLimitEs(limit: number | null): string {
  if (limit === null) {
    return "Ilimitado";
  }
  return limit.toString();
}
